import {
  AddIcon,
  AiIcon,
  Alert,
  Button,
  CalendarIcon,
  Card,
  CardContent,
  CardHeader,
  CardResource,
  CommunicationIcon,
  DocumentIcon,
  EmailAddressIcon,
  EmptyState,
  LinearProgress,
  PendingActionsIcon,
  PhoneIcon,
  Stack,
  SuggestionItem,
  SuggestionList,
  Tag,
  Typography,
  ViewIcon,
} from '@all-manager/design-system-v2';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type {
  CreateDocumentAction,
  CreateEventAction,
  CreatePendingItemAction,
  ExistingMatch,
  InboxEmail,
} from '../../types/inbox.types';
import SubTopicAutocomplete from '../../../topics/external-components/SubTopicAutocomplete/SubTopicAutocomplete';
import AddQuoteReceivedActionModal from '../../../topics/components/actionModales/AddQuoteReceivedActionModal/AddQuoteReceivedActionModal';
import AddUpdateContractModal from '../../../providers/components/AddUpdateContractModal/AddUpdateContractModal';
import CreateUpdateEventModal from '../../../events/external-components/CreateUpdateEventModal/CreateUpdateEventModal';
import AddPendingItemModal from '../../../topics/components/PendingItemsSection/AddPendingItemModal';
import CreateCommunicationFromInboxModal from '../CreateCommunicationFromInboxModal/CreateCommunicationFromInboxModal';
import CreateDocumentFromInboxModal from '../CreateDocumentFromInboxModal/CreateDocumentFromInboxModal';
import CreateSubTopicFromInboxModal from '../CreateSubTopicFromInboxModal/CreateSubTopicFromInboxModal';
import TopicRecommendationSection from '../TopicRecommendationSection/TopicRecommendationSection';
import ExistingMatchPreview from '../ExistingMatchPreview/ExistingMatchPreview';
import { getRequiredActions } from '../../utils/workflowSteps';
import { useGuidedProcessingWorkflow, type GroupedProvider } from './useGuidedProcessingWorkflow';
import {
  getDetectedActionsSummary,
  getExistingMatchTagLabel,
  getProgressLabel,
  getProviderActionLabel,
  getSectionTag,
  getSuggestionStatus,
  pluralize,
} from './workflowLabels';
import { CIVILITY_LABELS } from '../../../providers/constants/civilities';

type Props = {
  email: InboxEmail;
  spaceId: string;
  onSuccess?: () => void;
  activeStepId?: 'topic' | 'providers' | 'documents' | 'events' | 'pendingItems' | 'final';
  hideHeader?: boolean;
};

const HEADING_MAPPING = { h5: 'h3' };

const amountFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function formatEventDate(date: string | undefined): string {
  return format(new Date(date || Date.now()), "EEEE d MMMM yyyy 'à' HH'h'mm", { locale: fr });
}

function SectionTag(props: { isDone: boolean; countLabel: string }) {
  const tag = getSectionTag(props.isDone, props.countLabel);

  return <Tag color={tag.color} label={tag.label} variantIcon={tag.isState ? 'state' : undefined} />;
}

function ExistingMatchTag(props: { match: ExistingMatch }) {
  const label = getExistingMatchTagLabel(props.match);

  return props.match.source === 'ai' ? (
    <Tag color="ai" icon={<AiIcon />} label={label} />
  ) : (
    <Tag color="default" label={label} />
  );
}

export default function GuidedProcessingWorkflow(props: Props) {
  const hook = useGuidedProcessingWorkflow(props);

  const activeSectionIds = [
    hook.groupedProviders.length > 0 ? 1 : null,
    hook.documentActions.length > 0 ? 2 : null,
    hook.eventActions.length > 0 ? 3 : null,
    hook.pendingItemActions.length > 0 ? 4 : null,
  ].filter((id): id is number => id !== null);

  const totalSections = activeSectionIds.length;
  const completedSectionsCount = activeSectionIds.filter((id) => hook.completedSteps.includes(id)).length;
  const progressPercent = totalSections > 0 ? Math.round((completedSectionsCount / totalSections) * 100) : 100;

  const requiredActions = getRequiredActions(props.email.parsedActions || []);

  const showAll = !props.activeStepId;
  const showHeader = !props.hideHeader && showAll;
  const showTopic = showAll || props.activeStepId === 'topic';
  const showProviders = (showAll || props.activeStepId === 'providers') && hook.groupedProviders.length > 0;
  const showDocuments = (showAll || props.activeStepId === 'documents') && hook.documentActions.length > 0;
  const showEvents = (showAll || props.activeStepId === 'events') && hook.eventActions.length > 0;
  const showPendingItems = (showAll || props.activeStepId === 'pendingItems') && hook.pendingItemActions.length > 0;
  const showFinal = showAll || props.activeStepId === 'final';

  function renderProvider(group: GroupedProvider) {
    return (
      <SuggestionItem
        key={group.providerName}
        title={group.providerName}
        status={getSuggestionStatus(group)}
        tags={
          group.isExisting ? (
            <Tag color="providers" label="Déjà dans le carnet" />
          ) : (
            <Tag color="default" label="Nouveau prestataire" />
          )
        }
        meta={group.description ? `Métier : ${group.description}` : undefined}
        primaryAction={{
          label: getProviderActionLabel(group),
          loading: hook.loadingActionKey === `grouped-provider-${group.providerName}`,
          onClick: function handleCreateProvider() {
            hook.handleCreateGroupedProvider(group);
          },
        }}
        onStatusToggle={function handleToggle() {
          if (group.isCompleted) {
            hook.persistMultipleActionsCompletion(group.sourceActions, false);
          } else {
            hook.persistMultipleActionsIgnore(group.sourceActions, !group.isIgnored);
          }
        }}
      >
        {group.contacts.length > 0 && (
          <Stack spacing={1}>
            <Typography variant="body2Emphasis">{pluralize(group.contacts.length, 'contact')}</Typography>
            {group.contacts.map((contact) => (
              <Stack key={`${contact.name}-${contact.email ?? contact.phone ?? ''}`} spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center" wrap="wrap">
                  <Typography variant="body1">
                    {contact.civility ? `${CIVILITY_LABELS[contact.civility]} ${contact.name}` : contact.name}
                  </Typography>
                  {contact.role && <Tag color="default" label={contact.role} />}
                  {contact.existingMatch && <ExistingMatchTag match={contact.existingMatch} />}
                </Stack>
                {(contact.phone || contact.email) && (
                  <Stack direction="row" spacing={2} wrap="wrap">
                    {contact.phone && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2">{contact.phone}</Typography>
                      </Stack>
                    )}
                    {contact.email && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <EmailAddressIcon fontSize="small" color="action" />
                        <Typography variant="body2">{contact.email}</Typography>
                      </Stack>
                    )}
                  </Stack>
                )}
                {contact.existingMatch && <ExistingMatchPreview match={contact.existingMatch} />}
              </Stack>
            ))}
          </Stack>
        )}
      </SuggestionItem>
    );
  }

  function renderContract(doc: CreateDocumentAction) {
    const cd = doc.contractData;
    const hasGeneratedData = Boolean(
      cd && (cd.price || cd.period_of_notice || cd.description || cd.start_at || cd.end_at)
    );

    return (
      <SuggestionItem
        key={`contract-${doc.storagePath || doc.filename}`}
        title={cd?.name || doc.title || doc.filename}
        status={getSuggestionStatus(doc)}
        doneLabel="Contrat créé"
        tags={
          <>
            <Tag color="documents" icon={<DocumentIcon />} label="Contrat" />
            {hasGeneratedData && <Tag color="ai" icon={<AiIcon />} label="Données extraites" />}
          </>
        }
        meta={
          <>
            Fichier : <strong>{doc.filename}</strong>
            {doc.suggestedProviderName ? ` · Prestataire : ${doc.suggestedProviderName}` : ''}
            {cd?.price ? (
              <>
                {' · Tarif : '}
                <strong>{cd.price}</strong>
              </>
            ) : doc.suggestedAmount != null ? (
              <>
                {' · Montant : '}
                <strong>{amountFormatter.format(doc.suggestedAmount)}</strong>
              </>
            ) : null}
            {cd?.has_automatic_renewal
              ? ' · Tacite reconduction'
              : cd?.end_at
              ? ` · Échéance : ${cd.end_at}`
              : ''}
          </>
        }
        primaryAction={{
          label: 'Enregistrer le contrat',
          loading: hook.loadingActionKey === `contract-${doc.filename}`,
          onClick: function handleCreateContract() {
            hook.handleOpenContractModal(doc);
          },
        }}
        secondaryAction={
          doc.storagePath
            ? {
                label: 'Aperçu',
                icon: <ViewIcon />,
                onClick: function handlePreview() {
                  hook.handlePreviewDocument(doc.storagePath);
                },
              }
            : undefined
        }
        onStatusToggle={function handleToggle() {
          if (doc.isCompleted) {
            hook.persistSpecificActionCompletion(doc, false);
          } else {
            hook.persistSpecificActionIgnore(doc, !doc.isIgnored);
          }
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="text"
            size="small"
            color="default"
            onClick={function handleConvertToQuote() {
              hook.handleOpenQuoteModal(doc);
            }}
          >
            Enregistrer plutôt comme devis
          </Button>
        </Stack>
      </SuggestionItem>
    );
  }

  function renderDocument(doc: CreateDocumentAction, isQuote: boolean) {
    const hasGeneratedDescription = Boolean(
      isQuote && (doc.description?.trim() || (hook.quoteActions.length === 1 && props.email.document_summary?.trim()))
    );

    return (
      <SuggestionItem
        key={`${isQuote ? 'quote' : 'doc'}-${doc.storagePath || doc.filename}`}
        title={doc.title || doc.filename}
        status={getSuggestionStatus(doc)}
        doneLabel={isQuote ? 'Devis créé' : 'Document créé'}
        tags={
          <>
            <Tag color="documents" icon={<DocumentIcon />} label={isQuote ? 'Devis' : 'Document'} />
            {hasGeneratedDescription && <Tag color="ai" icon={<AiIcon />} label="Description générée" />}
          </>
        }
        meta={
          <>
            Fichier : <strong>{doc.filename}</strong>
            {doc.suggestedProviderName ? ` · Prestataire : ${doc.suggestedProviderName}` : ''}
            {isQuote && doc.suggestedAmount != null && (
              <>
                {' · Montant : '}
                <strong>{amountFormatter.format(doc.suggestedAmount)}</strong>
              </>
            )}
          </>
        }
        primaryAction={{
          label: isQuote ? 'Enregistrer le devis' : 'Ajouter aux documents',
          loading: hook.loadingActionKey === `doc-${doc.filename}`,
          onClick: function handleCreateDocument() {
            if (isQuote) {
              hook.handleOpenQuoteModal(doc);
            } else {
              hook.handleOpenDocumentModal(doc);
            }
          },
        }}
        secondaryAction={
          doc.storagePath
            ? {
                label: 'Aperçu',
                icon: <ViewIcon />,
                onClick: function handlePreview() {
                  hook.handlePreviewDocument(doc.storagePath);
                },
              }
            : undefined
        }
        onStatusToggle={function handleToggle() {
          if (doc.isCompleted) {
            hook.persistSpecificActionCompletion(doc, false);
          } else {
            hook.persistSpecificActionIgnore(doc, !doc.isIgnored);
          }
        }}
      />
    );
  }

  function renderEvent(event: CreateEventAction) {
    return (
      <SuggestionItem
        key={`event-${event.title}-${event.date}`}
        title={event.title}
        status={getSuggestionStatus(event)}
        doneLabel="Planifié"
        tags={
          <>
            <Tag color="rendezVous" icon={<CalendarIcon />} label="Rendez-vous" />
            {event.existingMatch && <ExistingMatchTag match={event.existingMatch} />}
          </>
        }
        meta={
          <>
            Date : <strong>{formatEventDate(event.date)}</strong>
            {event.suggestedProviderName ? ` · Prestataire : ${event.suggestedProviderName}` : ''}
          </>
        }
        primaryAction={{
          label: 'Planifier ce rendez-vous',
          loading: hook.loadingActionKey === `event-${event.title}`,
          onClick: function handlePlanEvent() {
            hook.handleOpenEventModal(event);
          },
        }}
        onStatusToggle={function handleToggle() {
          if (event.isCompleted) {
            hook.persistSpecificActionCompletion(event, false);
          } else {
            hook.persistSpecificActionIgnore(event, !event.isIgnored);
          }
        }}
      >
        {(event.existingMatch || event.description) && (
          <Stack spacing={2}>
            {event.description && (
              <Stack spacing={0.5}>
                {event.description
                  .split('\n')
                  .filter((line) => line.trim())
                  .map((line, index) => (
                    <Typography key={index} variant="body2">
                      {line}
                    </Typography>
                  ))}
              </Stack>
            )}
            {event.existingMatch && <ExistingMatchPreview match={event.existingMatch} />}
          </Stack>
        )}
      </SuggestionItem>
    );
  }

  function renderPendingItem(pendingItem: CreatePendingItemAction) {
    return (
      <SuggestionItem
        key={`pending-${pendingItem.title}`}
        title={pendingItem.title}
        status={getSuggestionStatus(pendingItem)}
        tags={
          <>
            <Tag color="pending_item" icon={<PendingActionsIcon />} label="À suivre" />
            {pendingItem.existingMatch && <ExistingMatchTag match={pendingItem.existingMatch} />}
          </>
        }
        meta={pendingItem.suggestedProviderName ? `Prestataire : ${pendingItem.suggestedProviderName}` : undefined}
        primaryAction={{
          label: 'Ajouter à suivre',
          loading: hook.loadingActionKey === `pending-${pendingItem.title}`,
          onClick: function handleAddPendingItem() {
            hook.handleOpenPendingItemModal(pendingItem);
          },
        }}
        onStatusToggle={function handleToggle() {
          if (pendingItem.isCompleted) {
            hook.persistSpecificActionCompletion(pendingItem, false);
          } else {
            hook.persistSpecificActionIgnore(pendingItem, !pendingItem.isIgnored);
          }
        }}
      >
        {pendingItem.existingMatch && <ExistingMatchPreview match={pendingItem.existingMatch} />}
      </SuggestionItem>
    );
  }

  function renderFinalAlert() {
    switch (hook.finalizationState) {
      case 'pendingActions':
        return (
          <Alert severity="info">
            Pensez à vérifier ou traiter les éléments ci-dessus avant de marquer cet e-mail comme traité.
          </Alert>
        );
      case 'missingTopic':
        return <Alert severity="warning">Pensez à associer un sujet avant de marquer cet e-mail comme traité.</Alert>;
      case 'allIgnored':
        return (
          <Alert severity="success">
            Toutes les actions détectées ont été ignorées. Vous pouvez finaliser cet e-mail.
          </Alert>
        );
      case 'ready':
        return <Alert severity="success">Toutes les étapes ont été validées. Vous pouvez finaliser cet e-mail.</Alert>;
    }
  }

  return (
    <Stack spacing={3}>
      {showHeader && (
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
            <Stack spacing={0.5}>
              <Typography variant="h5" variantMapping={HEADING_MAPPING}>
                Guide de traitement
              </Typography>
              <Typography variant="body2">
                {getDetectedActionsSummary(requiredActions.length, totalSections)}
              </Typography>
            </Stack>
            <Tag
              color={totalSections === 0 || completedSectionsCount === totalSections ? 'done' : 'pending'}
              variantIcon="state"
              label={getProgressLabel(completedSectionsCount, totalSections)}
            />
          </Stack>
          {totalSections > 0 && (
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              aria-label={getProgressLabel(completedSectionsCount, totalSections)}
            />
          )}
        </Stack>
      )}

      {showTopic && (
        <Stack spacing={2}>
          <Card>
            <CardHeader
              title="Sujet associé"
              subheader="Ce sujet servira de rattachement pour les devis, rendez-vous et communications."
              action={
                <Button variant="text" size="small" color="default" startIcon={<AddIcon />} onClick={hook.handleOpenNewSubTopicModal}>
                  Nouveau sujet
                </Button>
              }
            />
            <CardContent>
              <SubTopicAutocomplete
                label="Sélectionner le sujet"
                size="small"
                fullWidth
                value={hook.currentSubTopicId || null}
                onChange={hook.handleSubTopicChange}
              />
            </CardContent>
          </Card>

          <TopicRecommendationSection
            recommendation={hook.topicRecommendation}
            currentSubTopicId={hook.currentSubTopicId}
            onSelectSubTopic={hook.handleSubTopicChange}
            onCreateNewTopicClick={hook.handleOpenNewSubTopicModal}
          />
        </Stack>
      )}

      {showAll && totalSections === 0 && (
        <EmptyState
          title="Aucune action à traiter"
          subtitle="Cet e-mail ne contient ni devis, ni prestataire, ni rendez-vous à enregistrer. Vous pouvez directement le marquer comme traité."
        />
      )}

      {showProviders && (
        <CardResource
          variant="providers"
          title="Prestataires et contacts"
          action={
            <SectionTag
              isDone={hook.completedSteps.includes(1)}
              countLabel={pluralize(hook.groupedProviders.length, 'prestataire')}
            />
          }
          disableCardContent
        >
          <SuggestionList aria-label="Prestataires détectés">{hook.groupedProviders.map(renderProvider)}</SuggestionList>
        </CardResource>
      )}

      {showDocuments && (
        <CardResource
          variant="documents"
          title="Documents et devis"
          action={
            <SectionTag
              isDone={hook.completedSteps.includes(2)}
              countLabel={pluralize(hook.documentActions.length, 'document')}
            />
          }
          disableCardContent
        >
          <SuggestionList aria-label="Documents détectés">
            {hook.contractActions.map(renderContract)}
            {hook.quoteActions.map((quote) => renderDocument(quote, true))}
            {hook.otherDocActions.map((doc) => renderDocument(doc, false))}
          </SuggestionList>
        </CardResource>
      )}

      {showEvents && (
        <CardResource
          variant="rendezVous"
          title="Rendez-vous et interventions"
          action={
            <SectionTag
              isDone={hook.completedSteps.includes(3)}
              countLabel={pluralize(hook.eventActions.length, 'rendez-vous', 'rendez-vous')}
            />
          }
          disableCardContent
        >
          <SuggestionList aria-label="Rendez-vous détectés">{hook.eventActions.map(renderEvent)}</SuggestionList>
        </CardResource>
      )}

      {showPendingItems && (
        <CardResource
          variant="pending_item"
          title="Éléments à suivre"
          action={
            <SectionTag
              isDone={hook.completedSteps.includes(4)}
              countLabel={pluralize(hook.pendingItemActions.length, 'élément', 'éléments')}
            />
          }
          disableCardContent
        >
          <SuggestionList aria-label="Éléments à suivre détectés">
            {hook.pendingItemActions.map(renderPendingItem)}
          </SuggestionList>
        </CardResource>
      )}

      {showFinal && (
        <Card>
          <CardHeader
            title="Finalisation"
            subheader="Une fois les ressources nécessaires créées, marquez cet e-mail comme traité pour le retirer de la boîte de réception."
          />
          <CardContent>
            <Stack spacing={2} alignItems="flex-start">
              {renderFinalAlert()}
              <Button
                variant="text"
                size="small"
                color="default"
                startIcon={<CommunicationIcon />}
                onClick={hook.handleOpenCommunicationModal}
              >
                Ajouter une communication (facultatif)
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {hook.contractModalState && (
        <AddUpdateContractModal
          onClose={hook.handleCloseContractModal}
          providerId={hook.contractModalState.providerId}
          showProviderSelect={hook.contractModalState.showProviderSelect}
          initialValues={hook.contractModalState.initialValues}
          initialDocument={hook.contractModalState.initialDocument}
          isAiPreFilled={true}
          onSuccess={hook.handleContractModalSuccess}
        />
      )}

      {hook.quoteModalState && (
        <AddQuoteReceivedActionModal
          open={Boolean(hook.quoteModalState)}
          onClose={hook.handleCloseQuoteModal}
          subjectId={hook.quoteModalState.subjectId}
          initialValues={hook.quoteModalState.initialValues}
          onSuccess={hook.handleQuoteModalSuccess}
        />
      )}

      {hook.documentModalAction && (
        <CreateDocumentFromInboxModal
          open={Boolean(hook.documentModalAction)}
          onClose={hook.handleCloseDocumentModal}
          action={hook.documentModalAction}
          spaceId={props.spaceId}
          onSuccess={hook.handleDocumentModalSuccess}
        />
      )}

      {hook.eventModalState && (
        <CreateUpdateEventModal
          onClose={hook.handleCloseEventModal}
          subTopicId={hook.eventModalState.subTopicId}
          providerId={hook.eventModalState.providerId}
          initialValues={hook.eventModalState.initialValues}
          onSuccess={hook.handleEventModalSuccess}
        />
      )}

      {hook.pendingItemModalState && (
        <AddPendingItemModal
          open={Boolean(hook.pendingItemModalState)}
          isAdding={hook.isAddingPendingItem}
          onClose={hook.handleClosePendingItemModal}
          onAdd={hook.handlePendingItemModalAdd}
          initialTitle={hook.pendingItemModalState.initialTitle}
          initialProviderId={hook.pendingItemModalState.initialProviderId}
          initialProviderName={hook.pendingItemModalState.initialProviderName}
        />
      )}

      {hook.isCommunicationModalOpen && (
        <CreateCommunicationFromInboxModal
          open={hook.isCommunicationModalOpen}
          onClose={hook.handleCloseCommunicationModal}
          email={props.email}
          spaceId={props.spaceId}
          initialSubTopicId={hook.currentSubTopicId}
          onSuccess={hook.handleCommunicationModalSuccess}
        />
      )}

      {hook.isNewSubTopicModalOpen && (
        <CreateSubTopicFromInboxModal
          open={hook.isNewSubTopicModalOpen}
          onClose={hook.handleCloseNewSubTopicModal}
          initialTitle={hook.topicRecommendation?.suggestedNewTopicTitle}
          onSuccess={hook.handleNewSubTopicSuccess}
        />
      )}
    </Stack>
  );
}
